import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import ConversationBriefCard from './ConversationBriefCard';

export default function AIDebugPanel() {
  const {
    aiDebugEnabled,
    setAiDebugEnabled,
    pipelineEvents,
    setPipelineEvents,
    latestDebugPayload,
    setLatestDebugPayload,
  } = useAppContext();

  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('subsystems');
  const [expandedTrace, setExpandedTrace] = useState({});
  const [importedData, setImportedData] = useState('');
  
  if (!aiDebugEnabled) return null;

  // Use imported debug payload if in Replay mode, otherwise use the live backend one
  const debug = latestDebugPayload || {};
  const health = debug.pipeline_health || {};
  const contract = debug.compiled_contract || {};
  const timeline = debug.tool_timeline || [];
  const traceList = debug.decision_trace || pipelineEvents || [];
  const stateDiff = debug.state_diff || {};
  const brief = debug.conversation_brief || null;

  const handleExport = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(debug, null, 2));
    const dlAnchor = document.createElement('a');
    dlAnchor.setAttribute("href", dataStr);
    dlAnchor.setAttribute("download", `career_os_replay_${Date.now()}.json`);
    document.body.appendChild(dlAnchor);
    dlAnchor.click();
    dlAnchor.remove();
  };

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importedData);
      setLatestDebugPayload(parsed);
      setPipelineEvents(parsed.decision_trace || []);
      alert("Replay payload loaded successfully!");
    } catch (e) {
      alert("Invalid JSON format: " + e.message);
    }
  };

  const toggleTrace = (idx) => {
    setExpandedTrace(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div 
      className={`fixed z-50 flex flex-col bg-slate-905/95 backdrop-blur-md border border-slate-800 rounded-xl shadow-2xl transition-all duration-300 font-sans text-slate-200 overflow-hidden ${
        isOpen 
          ? 'bottom-20 left-4 right-4 md:bottom-4 md:left-auto md:right-4 w-[calc(100%-32px)] md:w-[420px] h-[65vh] md:h-[550px]' 
          : 'bottom-20 right-4 md:bottom-4 w-40 md:w-48 h-10'
      }`} 
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-950 border-b border-slate-800 shrink-0 select-none">
        <div className="flex items-center gap-2">
          <span className="animate-pulse w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Career OS Kernel</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setIsOpen(!isOpen)} className="text-slate-400 hover:text-slate-200 text-xs font-medium">
            {isOpen ? 'Minimize' : 'Expand Debug'}
          </button>
          <button onClick={() => setAiDebugEnabled(false)} className="text-red-400 hover:text-red-300 text-xs">✕</button>
        </div>
      </div>

      {isOpen && (
        <>
          {/* Navigation Tabs */}
          <div className="flex border-b border-slate-800 bg-slate-950/50 text-[11px] font-semibold shrink-0 overflow-x-auto whitespace-nowrap scrollbar-none">
            {['subsystems', 'brief', 'contract', 'trace', 'diff', 'replay'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 text-center capitalize border-b-2 hover:bg-slate-800/40 transition-colors shrink-0 ${activeTab === tab ? 'border-violet-500 text-violet-400 bg-violet-500/5' : 'border-transparent text-slate-400'}`}
              >
                {tab === 'diff' ? 'Diff' : tab === 'brief' ? '🧠 Brief' : tab}
              </button>
            ))}
          </div>

          {/* Tab Content Panels */}
          <div className="flex-1 overflow-y-auto p-4 text-xs space-y-4">
            
            {/* Panel: Subsystems & Timeline */}
            {activeTab === 'subsystems' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-xs font-bold text-slate-400 mb-2.5">Pipeline Subsystems Health</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'UserProfileResolver', label: 'Profile Resolver' },
                      { key: 'BrainRuntimeStore_load', label: 'Runtime Load' },
                      { key: 'CapabilityRegistry', label: 'Capability Registry' },
                      { key: 'KnowledgeResolver', label: 'Knowledge Resolver' },
                      { key: 'ConversationPlanner', label: 'Planner' },
                      { key: 'InstructionCompiler', label: 'Compiler' },
                      { key: 'LLM', label: 'LLM Engine' },
                      { key: 'ToolVerifier', label: 'Tool Verifier' },
                      { key: 'MutationEngine', label: 'Mutation Engine' },
                      { key: 'BrainRuntimeStore_save', label: 'Runtime Save' },
                    ].map(sub => {
                      const status = health[sub.key] || 'idle';
                      return (
                        <div key={sub.key} className="flex items-center gap-2 p-2 rounded-lg bg-slate-950/40 border border-slate-800/50">
                          <span className="text-sm">
                            {status === 'success' ? '🟢' : status === 'failed' ? '🔴' : status === 'started' ? '🟡' : '⚪'}
                          </span>
                          <span className="text-[11px] font-medium text-slate-300">{sub.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-slate-400 mb-2">Active Tool Timeline</h3>
                  {timeline.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 bg-slate-950/20 border border-dashed border-slate-800 rounded-lg">No active tools executed</div>
                  ) : (
                    timeline.map((tool, idx) => (
                      <div key={idx} className="p-3 bg-slate-950/30 border border-slate-800 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[11px] font-bold text-violet-400">{tool.tool_name}</span>
                          <span className="text-[10px] text-slate-500">Milestones</span>
                        </div>
                        <div className="flex items-center gap-1.5 pt-1">
                          {tool.milestones.map((m, mIdx) => (
                            <React.Fragment key={mIdx}>
                              <div className="flex flex-col items-center flex-1">
                                <span className={`text-[9px] font-mono px-1 rounded ${m.status === 'success' ? 'bg-emerald-500/20 text-emerald-400' : m.status === 'failed' ? 'bg-red-500/20 text-red-400' : m.status === 'skipped' ? 'bg-slate-800 text-slate-500' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                  {m.stage}
                                </span>
                              </div>
                              {mIdx < tool.milestones.length - 1 && <span className="text-slate-700">→</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Panel: Mission Brief */}
            {activeTab === 'brief' && (
              <div className="space-y-1">
                <ConversationBriefCard brief={brief} />
              </div>
            )}

            {/* Panel: Live Contract */}
            {activeTab === 'contract' && (
              <div className="space-y-3.5">
                <div className="grid grid-cols-2 gap-2 bg-slate-950/40 border border-slate-800 p-3 rounded-lg font-mono text-[11px]">
                  <div><span className="text-slate-500 text-[10px]">Mission:</span> <div className="text-slate-300 font-bold truncate">{contract.mission || 'None'}</div></div>
                  <div><span className="text-slate-500 text-[10px]">Section:</span> <div className="text-slate-300 font-bold truncate">{contract.section || 'None'}</div></div>
                  <div><span className="text-slate-500 text-[10px]">Goal:</span> <div className="text-slate-300 font-bold truncate">{contract.current_goal || 'None'}</div></div>
                  <div><span className="text-slate-500 text-[10px]">Capability:</span> <div className="text-slate-300 font-bold truncate">{contract.capability || 'None'}</div></div>
                  <div className="col-span-2"><span className="text-slate-500 text-[10px]">Target Field:</span> <div className="text-violet-400 font-bold truncate">{contract.target || 'None'}</div></div>
                </div>

                <div className="space-y-1.5 bg-slate-950/30 p-3 border border-slate-800/80 rounded-lg">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">Allowed Tools</span>
                  {contract.allowed_tools?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {contract.allowed_tools.map((t, idx) => (
                        <span key={idx} className="font-mono text-[10px] bg-slate-800/70 border border-slate-700 px-1.5 py-0.5 rounded text-slate-300">{t}</span>
                      ))}
                    </div>
                  ) : <span className="text-slate-500 font-mono text-[10px]">No restricted tools</span>}
                </div>
                
                <div className="space-y-1 bg-slate-950/20 p-2.5 border border-slate-800/50 rounded-lg font-mono text-[10px]">
                  <span className="text-[10px] font-bold text-slate-400">Current Question Why:</span>
                  <p className="text-slate-400 italic text-slate-400">"{contract.question || 'No pending questions'}"</p>
                </div>
              </div>
            )}

            {/* Panel: Decision Trace logs */}
            {activeTab === 'trace' && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-slate-400 mb-2">Turn Execution Steps</h3>
                {traceList.map((t, idx) => {
                  const isExp = expandedTrace[idx];
                  return (
                    <div key={idx} className="border border-slate-800 bg-slate-950/40 rounded-lg overflow-hidden transition-all duration-200">
                      <button onClick={() => toggleTrace(idx)} className="w-full flex items-center justify-between p-2.5 text-left font-mono hover:bg-slate-800/30 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase px-1 rounded ${t.status === 'success' ? 'bg-emerald-500/20 text-emerald-400' : t.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {t.status}
                          </span>
                          <span className="font-bold text-slate-300">{t.stage}</span>
                        </div>
                        <span className="text-[10px] text-violet-400 font-bold">{t.duration_ms ? `${t.duration_ms}ms` : 'started'}</span>
                      </button>

                      {isExp && (
                        <div className="p-3 border-t border-slate-850 bg-slate-950/80 font-mono text-[10px] space-y-2.5 overflow-x-auto max-w-full">
                          {t.decision && <div><span className="text-slate-500">Why:</span> <span className="text-emerald-400 font-bold">{t.decision}</span></div>}
                          {t.reason && <div><span className="text-slate-500">Reason:</span> <span className="text-slate-300">{t.reason}</span></div>}
                          {t.input && (
                            <div>
                              <span className="text-slate-500 block mb-1">INPUT:</span>
                              <pre className="p-2 bg-slate-900 border border-slate-800 rounded max-h-48 overflow-y-auto text-slate-400 whitespace-pre-wrap">{JSON.stringify(t.input, null, 2)}</pre>
                            </div>
                          )}
                          {t.output && (
                            <div>
                              <span className="text-slate-500 block mb-1">OUTPUT:</span>
                              <pre className="p-2 bg-slate-900 border border-slate-800 rounded max-h-48 overflow-y-auto text-slate-300 whitespace-pre-wrap">{JSON.stringify(t.output, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Panel: State Changes */}
            {activeTab === 'diff' && (
              <div className="space-y-3.5">
                <h3 className="text-xs font-bold text-slate-400 mb-1">Visual Diff of Modified State</h3>
                {Object.keys(stateDiff).length === 0 ? (
                  <div className="p-4 text-center text-slate-500 bg-slate-950/20 border border-dashed border-slate-800 rounded-lg">No modifications detected in this turn</div>
                ) : (
                  <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                    {Object.entries(stateDiff).map(([keyPath, diffVal], idx) => {
                      const oldVal = diffVal && typeof diffVal === 'object' && 'old' in diffVal ? diffVal.old : diffVal;
                      const newVal = diffVal && typeof diffVal === 'object' && 'new' in diffVal ? diffVal.new : diffVal;
                      return (
                        <div key={idx} className="p-2.5 bg-slate-950/40 border border-slate-800 rounded-lg font-mono text-[10px] space-y-1">
                          <span className="text-slate-500 font-bold block truncate">{keyPath}</span>
                          <div className="grid grid-cols-1 gap-1">
                            {oldVal !== undefined && (
                              <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-1.5 rounded line-through truncate">
                                {String(oldVal)}
                              </div>
                            )}
                            {newVal !== undefined && (
                              <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-1.5 rounded truncate">
                                {String(newVal)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Panel: Replay Mode */}
            {activeTab === 'replay' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-slate-400">Save Session Payload</h3>
                  <p className="text-[10px] text-slate-500">Download the active debug and state package to share or reload offline.</p>
                  <button onClick={handleExport} className="w-full bg-violet-600 hover:bg-violet-700 active:bg-violet-800 transition-colors text-white font-bold py-2 px-3 rounded-lg text-xs">
                    📥 Export Debug Replay Package
                  </button>
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <h3 className="text-xs font-bold text-slate-400">Reload Replay Package</h3>
                  <textarea
                    value={importedData}
                    onChange={(e) => setImportedData(e.target.value)}
                    placeholder="Paste the exported career_os_replay.json file contents here..."
                    className="w-full h-32 p-2 bg-slate-950 border border-slate-800 rounded-lg font-mono text-[10px] text-slate-300 focus:outline-none focus:border-violet-500"
                  />
                  <button onClick={handleImport} className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 py-2 px-3 rounded-lg text-xs font-semibold">
                    🔄 Import & Visualize Replay
                  </button>
                </div>
              </div>
            )}

          </div>
        </>
      )}
    </div>
  );
}
